import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Bell, AlertTriangle, ShieldAlert, Moon, Volume2, VolumeX } from "lucide-react";
import { playChime, notifyBrowser, inQuietWindow } from "./quietHours";
import { EmptyState } from "@/components/common";

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [muted, setMuted] = useState(() => localStorage.getItem("tharwy_alert_sound") === "off");
  const [prefs, setPrefs] = useState(null);
  const prefsRef = useRef(null);

  const loadPrefs = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications/preferences");
      setPrefs(data);
      prefsRef.current = data;
    } catch (e) { console.error(e); }
  }, []);

  const load = useCallback(async (initial = false) => {
    try {
      const { data: scan } = await api.post("/notifications/scan");
      const quiet = inQuietWindow(prefsRef.current);
      if (!initial && scan && scan.count > 0 && !quiet) {
        if (localStorage.getItem("tharwy_alert_sound") !== "off") playChime();
        notifyBrowser(scan.new_alerts);
      }
      const { data } = await api.get("/notifications");
      setNotes(data.notifications || []);
      setUnread(data.unread || 0);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    loadPrefs();
    load(true);
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
    const iv = setInterval(() => { loadPrefs(); load(false); }, 45000);
    return () => clearInterval(iv);
  }, [load, loadPrefs]);

  const quietActive = inQuietWindow(prefs);

  const toggleMute = (e) => {
    e.stopPropagation();
    const next = !muted;
    setMuted(next);
    localStorage.setItem("tharwy_alert_sound", next ? "off" : "on");
    if (!next) playChime();
  };

  const openMenu = async (open) => {
    if (open && unread > 0) {
      await api.post("/notifications/read-all");
      setUnread(0);
      setNotes((n) => n.map((x) => ({ ...x, read: true })));
    }
  };

  return (
    <DropdownMenu onOpenChange={openMenu}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative rounded-full nav-icon-btn" data-testid="notifications-bell">
          <Bell className="h-5 w-5" />
          {unread > 0 && (
            <span className="absolute -top-0.5 -end-0.5 h-4 min-w-4 px-1 rounded-full bg-[color:var(--tharwy-lime)] text-white text-[10px] font-semibold flex items-center justify-center"
              data-testid="notifications-unread-count">{unread}</span>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80">
        <DropdownMenuLabel className="flex items-center justify-between">
          <span className="flex items-center gap-1.5">
            Alerts &amp; Notifications
            {quietActive && (
              <span className="inline-flex items-center gap-0.5 text-[10px] font-medium text-[color:var(--tharwy-lime-700)]" data-testid="notifications-quiet-badge">
                <Moon className="h-3 w-3" /> Quiet
              </span>
            )}
          </span>
          <button onClick={toggleMute} className="text-muted-foreground hover:text-foreground transition-colors"
            title={muted ? "Unmute alert sound" : "Mute alert sound"} data-testid="notifications-mute-toggle">
            {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
          </button>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {notes.length === 0 ? (
          <div className="px-3" data-testid="notifications-empty">
            <EmptyState compact title="You're all caught up" hint="Alerts, fills and Shariah changes land here." />
          </div>
        ) : (
          <div className="max-h-80 overflow-auto">
            {notes.slice(0, 12).map((n) => {
              const danger = n.severity === "danger";
              const Icon = danger ? ShieldAlert : AlertTriangle;
              return (
                <button key={n.id} onClick={() => navigate(`/stock/${encodeURIComponent(n.symbol)}`)}
                  className="w-full text-start flex gap-2.5 px-3 py-2.5 hover:bg-accent transition-colors" data-testid={`notification-${n.symbol}`}>
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${danger ? "text-red-600" : "text-[color:var(--tharwy-lime-700)]"}`} />
                  <div>
                    <div className="text-sm font-medium">{n.title}</div>
                    <div className="text-xs text-muted-foreground">{n.message}</div>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
